import { useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { Fields, validationSchema } from './validation';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { ContractBillingStatusPascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';
import { NumberUtils } from '@/utils/numbers.utils';

interface IFormProps {
  onSubmit: (daysNumber: number | null) => void;
  onClose: () => void
  days?: number | null;
}

export default function DaysForm({
  onSubmit,
  onClose,
  days
}: IFormProps) {
  const { contractId }: { contractId: UUID } = useParams();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { cachedVariableContractLines, cachedBCContractLines } = useGetContractLinesFromCache(contractId);
  const billingIntervals = ['Monthly', 'Quarterly', 'HalfYearly'];

  const hasAnyLinePaused = useMemo(() => {
    const bcLines = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items || [];
    const variableLines = cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items || [];

    const bcLinesPaused = bcLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    const variableLinesPaused = variableLines.some(line =>
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
      line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    return bcLinesPaused || variableLinesPaused;
  }, [cachedBCContractLines, cachedVariableContractLines]);

  const hasPauseActive = hasAnyLinePaused;

  const getBillingInterval = useMemo(() => (): string => {
    for (const el of billingIntervals) {
      if (cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items?.some(
        (cl) => cl?.billingInterval === el)) {
        return el;
      }

      if (cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items?.some(
        (cl) => cl?.billingInterval === el)) {
        return el;
      }

      if (el === 'HalfYearly') {
        if (cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items?.some(
          (cl) => cl?.billingInterval === 'Half-Yearly')) {
          return 'HalfYearly';
        }
      }
    }

    return 'Annual';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedBCContractLines, cachedVariableContractLines]);

  const contractBillingInterval = getBillingInterval();

  return (
    <AgModalWindow
      isOpen={true}
      onClose={onClose}
      title="Days Before Renewal"
      contentStyle={{ width: 340 }}
    >
      <Formik<Fields>
        initialValues={{ daysBeforeRenewal: days }}
        validationSchema={validationSchema}
        onSubmit={() => setShowConfirmationModal(true)}
        validateOnMount={true}
        validate={(values) => {
          try {
            validationSchema.validateSync(values, { context: { contractBillingInterval } });
          } catch (err) {
            return { daysBeforeRenewal: (err as Error).message };
          }
          return {};
        }}
      >
        {({ values, errors, touched, isValid, dirty, setFieldValue, setFieldTouched }) => (
          <Form>
            <AgConfirmationModal
              isOpen={showConfirmationModal}
              title="You are about to modify Days Before Renewal for this contract"
              onClose={() => setShowConfirmationModal(false)}
              onConfirm={() => {
                onSubmit(NumberUtils.parseIntegerInput(String(values.daysBeforeRenewal)));
              }}
              contentStyle={{ width: 600 }}
            >
              You are about to modify Days Before Renewal for this contract.<br />
              This change could potentially impact Sales Order generation. <br />
              {hasPauseActive && (
                <>
                  <br />
                  <strong>Warning:</strong> This action will change the billing date.
                  A Pause to billing is set. Please review your Pause End Date to ensure accurate billing.<br />
                </>
              )}
              Please confirm you wish to continue.
            </AgConfirmationModal>
            <ModalFormTextInput
              id="daysBeforeRenewal"
              style={{ textAlign: 'right' }}
              labelTitle="Days"
              value={values.daysBeforeRenewal ?? undefined}
              onChange={(e) => {
                setFieldTouched('daysBeforeRenewal', true, false);
                setFieldValue('daysBeforeRenewal', e.target.value);
              }}
              type="number"
              validationMessage={
                (errors.daysBeforeRenewal && touched.daysBeforeRenewal) ? errors.daysBeforeRenewal : undefined
              }
            />

            <ActionButtonsWrapper
              leftButton={(
                <Button
                  buttonStyleType={ButtonStyleType.SECONDARY}
                  onClick={onClose}
                >
                  Cancel
                </Button>
              )}
              rightButton={(
                <Button
                  disabled={!isValid || !dirty}
                  buttonStyleType={ButtonStyleType.PRIMARY}
                  type="submit"
                >
                  Save
                </Button>
              )}
            />
          </Form>
        )}
      </Formik>
    </AgModalWindow>
  );
}
